import React, { useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Icon,
  SpaceBetween,
} from "@amzn/awsui-components-react";
import {
  AttachmentUploads,
  PendingAttachment,
} from "../../../hooks/useAttachmentUploads";
import {
  ACCEPT,
  MAX_ATTACHMENTS,
  MAX_ATTACHMENT_BYTES,
  filesFromTransfer,
  formatBytes,
  isImageType,
} from "./attachmentUtils";
import "./Attachments.scss";

/**
 * onPaste for a larger form around a dropzone (e.g. pasting into the conclusion summary): adds
 * pasted files, leaves text alone, and skips pastes the inner dropzone already handled.
 */
/** Called with the files just added and, for a paste into a textarea, its selection. */
export type OnFilesAdded = (
  items: PendingAttachment[],
  selection?: { start: number; end: number }
) => void;

export const attachmentPasteHandler =
  (uploads: AttachmentUploads, disabled?: boolean, onAdded?: OnFilesAdded) =>
  (e: React.ClipboardEvent) => {
    if (disabled || e.defaultPrevented) return;
    const files = filesFromTransfer(e.clipboardData);
    if (!files.length) return;
    e.preventDefault();
    const added = uploads.add(files);
    const target = e.target as HTMLTextAreaElement;
    const selection =
      target instanceof HTMLTextAreaElement
        ? { start: target.selectionStart, end: target.selectionEnd }
        : undefined;
    onAdded?.(added, selection);
  };

/** The picked files (thumbnail, name, progress, remove) plus the "Attach files" button. */
export const AttachmentPending: React.FC<{
  uploads: AttachmentUploads;
  disabled?: boolean;
}> = ({ uploads, disabled }) => {
  const input = useRef<HTMLInputElement>(null);
  const full = uploads.items.length >= MAX_ATTACHMENTS;
  return (
    <SpaceBetween size="xs">
      {uploads.rejected.length > 0 && (
        <Alert
          type="warning"
          dismissible
          onDismiss={uploads.dismissRejected}
          statusIconAriaLabel="Warning"
        >
          {uploads.rejected.join(" · ")}
        </Alert>
      )}
      {uploads.items.length > 0 && (
        <div className="attachment-strip" data-testid="attachment-pending">
          {uploads.items.map((item) => (
            <div
              key={item.id}
              className={`attachment-chip${
                item.status === "error" ? " attachment-chip--error" : ""
              }`}
              data-testid="attachment-chip"
            >
              {item.previewUrl && isImageType(item.contentType) ? (
                <img
                  className="attachment-chip__thumb"
                  src={item.previewUrl}
                  alt=""
                />
              ) : (
                <span className="attachment-chip__icon">
                  <Icon name="file" />
                </span>
              )}
              <div className="attachment-chip__text">
                <div className="attachment-chip__name" title={item.fileName}>
                  <Box fontSize="body-s">{item.fileName}</Box>
                </div>
                {item.status === "uploading" ? (
                  <div
                    className="attachment-chip__bar"
                    role="progressbar"
                    aria-label={`Uploading ${item.fileName}`}
                    aria-valuenow={Math.round(item.progress * 100)}
                  >
                    <div
                      style={{ width: `${Math.round(item.progress * 100)}%` }}
                    />
                  </div>
                ) : item.status === "error" ? (
                  <Box fontSize="body-s" color="text-status-error">
                    {item.error || "Upload failed"}
                  </Box>
                ) : (
                  <Box fontSize="body-s" color="text-body-secondary">
                    {formatBytes(item.size)}
                  </Box>
                )}
              </div>
              <Button
                variant="icon"
                iconName="close"
                ariaLabel={`Remove ${item.fileName}`}
                disabled={disabled}
                onClick={() => uploads.remove(item.id)}
              />
            </div>
          ))}
        </div>
      )}
      <SpaceBetween direction="horizontal" size="xs" alignItems="center">
        <Button
          variant="inline-link"
          iconName="upload"
          disabled={disabled || full}
          onClick={() => input.current?.click()}
        >
          Attach files
        </Button>
        <Box fontSize="body-s" color="text-body-secondary">
          or paste a screenshot / drop files · up to{" "}
          {formatBytes(MAX_ATTACHMENT_BYTES)} each
        </Box>
      </SpaceBetween>
      <input
        ref={input}
        type="file"
        multiple
        accept={ACCEPT}
        hidden
        data-testid="attachment-input"
        onChange={(e) => {
          const files = Array.from(e.target.files || []);
          e.target.value = "";
          if (files.length) uploads.add(files);
        }}
      />
    </SpaceBetween>
  );
};

interface AttachmentDropzoneProps {
  uploads: AttachmentUploads;
  disabled?: boolean;
  /** Pasted/dropped files (not ones picked with "Attach files"), e.g. to place images inline. */
  onFilesAdded?: OnFilesAdded;
  children: React.ReactNode;
}

/**
 * Wraps an editor (comment box, conclusion form) so a screenshot can be pasted (Ctrl/⌘+V) or a
 * file dropped anywhere on it; the "Attach files" button and the pending files render below.
 * Plain text pastes are left alone.
 */
export const AttachmentDropzone: React.FC<AttachmentDropzoneProps> = ({
  uploads,
  disabled,
  onFilesAdded,
  children,
}) => {
  const [dragging, setDragging] = useState(false);
  const depth = useRef(0);
  const hasFiles = (e: React.DragEvent) =>
    Array.from(e.dataTransfer?.types || []).includes("Files");
  return (
    <div
      className={`attachment-dropzone${
        dragging ? " attachment-dropzone--active" : ""
      }`}
      data-testid="attachment-dropzone"
      // Capture phase: the comment box (PromptInput) cancels every paste it sees, so files must
      // be taken before it; its own handler then finds no text and does nothing.
      onPasteCapture={attachmentPasteHandler(uploads, disabled, onFilesAdded)}
      onDragEnter={(e) => {
        if (disabled || !hasFiles(e)) return;
        e.preventDefault();
        depth.current += 1;
        setDragging(true);
      }}
      onDragOver={(e) => {
        if (disabled || !hasFiles(e)) return;
        e.preventDefault();
      }}
      onDragLeave={() => {
        depth.current = Math.max(0, depth.current - 1);
        if (depth.current === 0) setDragging(false);
      }}
      onDrop={(e) => {
        depth.current = 0;
        setDragging(false);
        if (disabled) return;
        const files = filesFromTransfer(e.dataTransfer);
        if (!files.length) return;
        e.preventDefault();
        onFilesAdded?.(uploads.add(files));
      }}
    >
      {dragging && (
        <div className="attachment-dropzone__overlay">
          <Box fontWeight="bold">Drop to attach</Box>
        </div>
      )}
      <SpaceBetween size="xs">
        {children}
        <AttachmentPending uploads={uploads} disabled={disabled} />
      </SpaceBetween>
    </div>
  );
};

export default AttachmentDropzone;
